import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  Box, Grid, Card, CardContent, CardMedia, Typography, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip,
  Paper, Divider, List, ListItem, ListItemText, ListItemSecondaryAction,
  FormControl, InputLabel, Select, MenuItem, Alert, Snackbar, Pagination, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  PersonAdd as PersonAddIcon,
  Payment as PaymentIcon,
  PauseCircleOutline as PauseIcon,
  ListAlt as ListIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  GridOn as GridOnIcon,
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { fetchAllProducts, createPOSOrder, createCustomer, searchCustomers, getSellerInfo, searchProductByBarcode, getNextBillNumber, fetchUnits, fetchSellerCategories } from '../api';
import { addHeldBill, loadHeldBills, getHeldBill, removeHeldBill } from '../utils/holdBills';

// Every cart line gets its own id: product/variant ids are not unique within the
// cart (the same variant can be added twice with different rates) and loose items
// have no product at all, so they cannot be used as a React key or lookup key.
let cartLineSequence = 0;
const createCartLineId = () =>
  `line-${Date.now().toString(36)}-${(cartLineSequence++).toString(36)}`;

// A line is "loose" (ad-hoc, not backed by the catalogue) when the cashier
// created it manually, or when it still carries the old placeholder ids
// ("0", "00", ...) written by earlier versions of this screen.
const isLooseCartItem = (item) => {
  if (!item) return false;
  if (item.is_loose_item === true || item.is_loose_item === 1 || item.is_loose_item === '1') return true;
  const hasProduct = item.product_id !== null && item.product_id !== undefined && item.product_id !== '' && Number(item.product_id) > 0;
  const hasVariant = item.product_variant_id !== null && item.product_variant_id !== undefined && item.product_variant_id !== '' && Number(item.product_variant_id) > 0;
  return !hasProduct && !hasVariant;
};

// Normalises a cart line (used for freshly added items and for bills restored
// from localStorage, which may have been written by an older build).
const normalizeCartItem = (item) => {
  const loose = isLooseCartItem(item);
  return {
    ...item,
    line_id: item.line_id || createCartLineId(),
    is_loose_item: loose,
    product_id: loose ? null : item.product_id,
    product_variant_id: loose ? null : item.product_variant_id,
  };
};

const normalizeCartItems = (items) => (Array.isArray(items) ? items.map(normalizeCartItem) : []);

export default function POS() {
  // Add global print styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        body * {
          visibility: hidden;
        }
        [data-print="bill"] {
          visibility: visible;
        }
        [data-print="bill"] * {
          visibility: visible;
        }
        @page {
          size: auto;
          margin: 0;
        }
        .bill-print-content {
          page-break-inside: avoid;
          max-height: 100vh;
          overflow: hidden;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerDialog, setCustomerDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentConfirmationDialog, setPaymentConfirmationDialog] = useState(false);
  const [billPrintDialog, setBillPrintDialog] = useState(false);
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [newCharge, setNewCharge] = useState({ name: '', amount: '' });
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [customer, setCustomer] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [multiplePayments, setMultiplePayments] = useState([]);
  const [showMultiplePayments, setShowMultiplePayments] = useState(false);
  const [tempPaymentMethod, setTempPaymentMethod] = useState('cash');
  const [tempPaymentAmount, setTempPaymentAmount] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [variantDialog, setVariantDialog] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [looseProductDialog, setLooseProductDialog] = useState(false);
  const [looseProduct, setLooseProduct] = useState({
    name: '',
    price: '',
    quantity: 1,
    unit: 'pc',
    unitName: 'piece',
    pricingMethod: 'per_unit' // 'per_unit' or 'total_rate'
  });
  const [units, setUnits] = useState([]);
  const [productDetailDialog, setProductDetailDialog] = useState(false);
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [holdName, setHoldName] = useState('');
  const [heldBills, setHeldBills] = useState([]);
  const [heldBillsDialog, setHeldBillsDialog] = useState(false);

  // Category grid state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [viewMode, setViewMode] = useState('categories'); // 'categories' or 'products'
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Dynamic products per page based on available space
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const containerRef = useRef(null);
  
  // Use ref to prevent multiple simultaneous product loads
  const isLoadingProductsRef = useRef(false);

  // Dynamic cart height based on available space
  const [cartItemsHeight, setCartItemsHeight] = useState(290);
  const cartContainerRef = useRef(null);

  // Function to load all products (only called on initial mount)
  const loadAllProducts = useCallback(async () => {
    // Prevent multiple simultaneous loads using ref
    if (isLoadingProductsRef.current) {
      console.log('Product load already in progress, skipping...');
      return;
    }
    
    isLoadingProductsRef.current = true;
    console.log('Starting product load...');
    
    try {
      // Fetch all products in a single API call
      const r = await fetchAllProducts();
      if (r && r.ok && r.data) {
        setProducts(r.data);
        console.log('Product load complete. Total products:', r.data.length);
      }
    } catch (err) {
      console.error('Error fetching all products:', err);
    } finally {
      isLoadingProductsRef.current = false;
    }
  }, []);

  // Function to load seller categories
  const loadCategories = useCallback(async () => {
    setIsLoadingCategories(true);
    try {
      const cats = await fetchSellerCategories();
      console.log('Fetched categories:', cats);
      if (cats && Array.isArray(cats)) {
        setCategories(cats);
        console.log('Categories loaded:', cats.length, cats);
      } else {
        console.warn('Categories response is not an array:', cats);
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      console.error('Error response:', err.response?.data);
      setCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  // Function to handle category selection
  const handleCategoryClick = useCallback((category) => {
    setSelectedCategory(category);
    setViewMode('products');
    setCurrentPage(1);
  }, []);

  // Function to go back to category grid
  const handleBackToCategories = useCallback(() => {
    setSelectedCategory(null);
    setViewMode('categories');
    setCurrentPage(1);
  }, []);

  // Function to update stock locally without reloading all products
  const updateProductStockLocally = useCallback((cartItems) => {
    setProducts(prevProducts => {
      return prevProducts.map(product => {
        // Find if this product is in the cart
        const cartItem = cartItems.find(item => item.product_id === product.id);
        if (!cartItem) return product;

        // Update the specific variant's stock
        return {
          ...product,
          variants: product.variants.map(variant => {
            if (variant.id === cartItem.product_variant_id && !product.is_unlimited_stock) {
              const newStock = Math.max(0, variant.stock - cartItem.quantity);
              return {
                ...variant,
                stock: newStock,
                status: newStock <= 0 ? 0 : variant.status
              };
            }
            return variant;
          })
        };
      });
    });
  }, []);

  useEffect(() => {
    // Load categories first, then products
    loadCategories().then(() => {
      loadAllProducts();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only load once on mount

  useEffect(() => {
    getSellerInfo().then(r => setSellerInfo(r.data)).catch(() => setSellerInfo(null));
  }, []);

  useEffect(() => {
    fetchUnits().then(r => {
      const fetchedUnits = r.data || [];
      setUnits(fetchedUnits);
      // Set default unit to first unit or 'pc' if available
      if (fetchedUnits.length > 0) {
        const defaultUnit = fetchedUnits.find(unit => unit.short_code === 'pc') || fetchedUnits[0];
        setLooseProduct(prev => ({
          ...prev,
          unit: defaultUnit.short_code,
          unitName: defaultUnit.name,
          pricingMethod: 'per_unit'
        }));
      }
    }).catch(() => setUnits([]));
  }, []);

  // Generate QR code for Play Store app
  useEffect(() => {
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.OwnTrip.app';
    QRCode.toDataURL(playStoreUrl, { width: 140, margin: 1 })
      .then(url => setQrCodeDataUrl(url))
      .catch(err => console.error('Error generating QR code:', err));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    setHeldBills(loadHeldBills());
  }, []);

  useEffect(() => {
    const holdId = location.state?.holdId;
    if (!holdId) return;

    const bill = getHeldBill(holdId);
    if (bill) {
      setCart(normalizeCartItems(bill.cart));
      setAdditionalCharges(bill.additionalCharges || []);
      setCustomer(bill.customer || { name: '', phone: '', email: '' });
      setSelectedCustomer(bill.selectedCustomer || null);
      setPaymentMethod(bill.paymentMethod || 'cash');
      setMultiplePayments(bill.multiplePayments || []);
      setShowMultiplePayments(!!bill.showMultiplePayments);
      removeHeldBill(holdId);
      setHeldBills(loadHeldBills());
      setSnackbar({ open: true, message: 'Held bill loaded. Continue billing.', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: 'Held bill not found.', severity: 'error' });
    }

    navigate('/pos', { replace: true, state: {} });
  }, [location.state, navigate]);

  // Customer search effect
  useEffect(() => {
    const searchCustomersDebounced = async () => {
      if (customerSearchTerm.length >= 2) {
        try {
          const response = await searchCustomers(customerSearchTerm);
          const results = response.data || [];
          setCustomerSearchResults(results);
          setShowCustomerDropdown(results.length > 0);

          // If no customers found and search term looks like a phone number, auto-open add customer dialog
          if (results.length === 0 && /^\d{10,}$/.test(customerSearchTerm.trim())) {
            setCustomer({
              name: '',
              phone: customerSearchTerm.trim(),
              email: ''
            });
            setCustomerDialog(true);
            setCustomerSearchTerm('');
            setShowCustomerDropdown(false);
          }
        } catch (error) {
          console.error('Error searching customers:', error);
          setCustomerSearchResults([]);
          setShowCustomerDropdown(false);
        }
      } else {
        setCustomerSearchResults([]);
        setShowCustomerDropdown(false);
      }
    };

    const timeoutId = setTimeout(searchCustomersDebounced, 300);
    return () => clearTimeout(timeoutId);
  }, [customerSearchTerm]);

  const filteredProducts = products
    .filter(product => {
      // If search term exists, filter by search
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = (
          product.name.toLowerCase().includes(searchLower) ||
          (product.barcode && product.barcode.toLowerCase().includes(searchLower)) ||
          (product.brand_name && product.brand_name.toLowerCase().includes(searchLower)) ||
          (product.type && product.type.toLowerCase().includes(searchLower))
        );
        return matchesSearch;
      }
      // If a category is selected, filter by that category
      if (selectedCategory) {
        return product.category_id === selectedCategory.id;
      }
      // If in products view, show all products
      if (viewMode === 'products') {
        return true;
      }
      // If in categories view and no category selected, hide products
      return false;
    })
    .sort((a, b) => {
      const aStock = a.variants?.[0]?.stock ?? 0;
      const bStock = b.variants?.[0]?.stock ?? 0;
      // Sort in-stock products (stock > 0) first
      if (aStock > 0 && bStock <= 0) return -1;
      if (aStock <= 0 && bStock > 0) return 1;
      return 0; // Keep original order for same stock status
    });

  // Group products by category for search results
  const productsByCategory = searchTerm ? filteredProducts.reduce((groups, product) => {
    const catName = product.category_name || 'Other';
    if (!groups[catName]) {
      groups[catName] = [];
    }
    groups[catName].push(product);
    return groups;
  }, {}) : {};

  // Helper functions for multiple payments
  const addPaymentMethod = () => {
    if (!tempPaymentAmount || parseFloat(tempPaymentAmount) <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid amount',
        severity: 'error'
      });
      return;
    }

    const amount = parseFloat(tempPaymentAmount);
    const totalPaid = multiplePayments.reduce((sum, p) => sum + p.amount, 0);
    const totalAmount = calculateFinalPayable();

    if (totalPaid + amount > totalAmount) {
      setSnackbar({
        open: true,
        message: 'Payment amount exceeds total order value',
        severity: 'error'
      });
      return;
    }

    const newPayment = {
      method: tempPaymentMethod,
      amount: amount,
      id: Date.now()
    };

    setMultiplePayments([...multiplePayments, newPayment]);
    setTempPaymentAmount('');
    
    // Check if fully paid
    if (totalPaid + amount >= totalAmount) {
      setShowMultiplePayments(false);
    }
  };

  const removePaymentMethod = (paymentId) => {
    setMultiplePayments(multiplePayments.filter(p => p.id !== paymentId));
  };

  const getPaymentMethodString = () => {
    if (multiplePayments.length === 0) {
      return paymentMethod;
    }
    return multiplePayments.map(p => `${p.method}:${p.amount}`).join(', ');
  };

  const getTotalPaidAmount = () => {
    return multiplePayments.reduce((sum, p) => sum + p.amount, 0);
  };

  const getRemainingAmount = () => {
    const total = calculateFinalPayable();
    const paid = getTotalPaidAmount();
    return Math.max(0, total - paid);
  };

  const getPaymentMethodDisplay = () => {
    if (multiplePayments.length > 0) {
      return multiplePayments.map(p => {
        const emoji = p.method === 'cash' ? '💵' : 
                     p.method === 'card' ? '💳' : 
                     p.method === 'upi' ? '📱' : '🏦';
        return `${emoji} ${p.method.toUpperCase()}: ₹${p.amount}`;
      }).join(', ');
    }
    
    const emoji = paymentMethod === 'cash' ? '💵' :
                 paymentMethod === 'card' ? '💳' :
                 paymentMethod === 'upi' ? '📱' : '🏦';
    const name = paymentMethod === 'cash' ? 'Cash' :
                paymentMethod === 'card' ? 'Card' :
                paymentMethod === 'upi' ? 'UPI' : 'Bank Transfer';
    return `${emoji} ${name}`;
  };

  useEffect(() => {
    const updateCartHeight = () => {
      if (cartContainerRef.current) {
        const containerHeight = cartContainerRef.current.offsetHeight;
        // Account for all cart elements: title, search, customer info, additional charges, discount, payment method, total, buttons
        const otherElementsHeight = 380; // More accurate calculation for all cart elements
        const availableHeight = Math.max(containerHeight - otherElementsHeight, 100); // minimum 100px
        setCartItemsHeight(availableHeight);
      }
    };

    if (cartContainerRef.current) {
      updateCartHeight();
    }

    window.addEventListener('resize', updateCartHeight);
    return () => window.removeEventListener('resize', updateCartHeight);
  }, []);

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const productWidth = 110; // card width
        const spacing = 8; // spacing between cards
        const productsPerRow = Math.floor((containerWidth + spacing) / (productWidth + spacing));
        const containerHeight = containerRef.current.offsetHeight;
        const productHeight = 140; // card height
        const spacingVertical = 8; // vertical spacing
        const rowsToShow = Math.floor((containerHeight + spacingVertical) / (productHeight + spacingVertical));
        const calculatedItems = productsPerRow * rowsToShow;
        setItemsPerPage(Math.max(calculatedItems, 6)); // minimum 6 products
      }
    };

    if (containerRef.current) {
      updateItemsPerPage();
    }

    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedProducts = filteredProducts.slice(startIndex, endIndex);

  const addToCart = useCallback((productOrVariant, productInfo = null) => {
    // Check if it's a variant (has product_id) or a product
    const isVariant = productOrVariant.product_id !== undefined;
    
    let productId, variantId, productName, unitPrice, measurement, unitName, unitShortCode, mrp, discountedPrice;
    
    if (isVariant) {
      // It's a variant
      productId = productOrVariant.product_id;
      variantId = productOrVariant.id;
      // Use productInfo if provided, otherwise fall back to scannedProduct
      const productNameSource = productInfo?.name || scannedProduct?.name || 'Product';
      productName = productNameSource;
      unitPrice = productOrVariant.effective_price;
      mrp = productOrVariant.price;
      discountedPrice = productOrVariant.discounted_price;
      measurement = productOrVariant.measurement;
      unitName = productOrVariant.unit_name;
      unitShortCode = productOrVariant.unit_short_code;
    } else {
      // It's a product
      productId = productOrVariant.id;
      variantId = productOrVariant.variants?.[0]?.id || null;
      productName = productOrVariant.name;
      unitPrice = productOrVariant.variants?.[0]?.effective_price || 0;
      mrp = productOrVariant.variants?.[0]?.price || 0;
      discountedPrice = productOrVariant.variants?.[0]?.discounted_price || 0;
      measurement = productOrVariant.variants?.[0]?.measurement;
      unitName = productOrVariant.variants?.[0]?.unit_name;
      unitShortCode = productOrVariant.variants?.[0]?.unit_short_code;
    }
    
    const existingItem = cart.find(item => 
      !item.is_loose_item &&
      item.product_id === productId &&
      (item.product_variant_id ?? null) === (variantId ?? null)
    );
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.line_id === existingItem.line_id
          ? { ...item, quantity: item.quantity + 1, total_price: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        line_id: createCartLineId(),
        is_loose_item: false,
        product_id: productId,
        product_variant_id: variantId ?? null,
        product_name: productName,
        quantity: 1,
        unit_price: unitPrice,
        total_price: unitPrice,
        mrp: mrp,
        discounted_price: discountedPrice,
        measurement: measurement,
        unit_name: unitName,
        unit_short_code: unitShortCode
      }]);
    }
  }, [cart, scannedProduct]);

  const updateQuantity = (lineId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(lineId);
      return;
    }
    
    // Round to 1 decimal place for loose items, whole numbers for others
    const item = cart.find(item => item.line_id === lineId);
    if (!item) return;
    const roundedQuantity = item.is_loose_item ? 
      Math.round(newQuantity * 10) / 10 : 
      Math.round(newQuantity);
    
    setCart(cart.map(cartItem =>
      cartItem.line_id === lineId
        ? { ...cartItem, quantity: roundedQuantity, total_price: roundedQuantity * cartItem.unit_price }
        : cartItem
    ));
  };

  const removeFromCart = (lineId) => {
    setCart(cart.filter(item => item.line_id !== lineId));
  };

  const getDefaultUnit = () => {
    if (units.length === 0) return { short_code: 'pc', name: 'Piece' };
    return units.find(unit => unit.short_code === 'pc') || units[0];
  };

  const addLooseProduct = () => {
    if (looseProduct.name && looseProduct.price && looseProduct.quantity > 0) {
      const quantity = parseFloat(looseProduct.quantity);
      const price = parseFloat(looseProduct.price);
      
      let unitPrice, totalPrice;
      
      if (looseProduct.pricingMethod === 'total_rate') {
        // Total rate for the quantity (e.g., 500g for ₹20)
        totalPrice = price;
        unitPrice = price / quantity;
      } else {
        // Price per unit (e.g., ₹0.04 per gram)
        unitPrice = price;
        totalPrice = price * quantity;
      }
      
      setCart([...cart, {
        // Loose items are not in the catalogue: they are stored with NULL
        // product/variant ids and are flagged so the backend never tries to
        // look up stock for them.
        line_id: createCartLineId(),
        is_loose_item: true,
        product_id: null,
        product_variant_id: null,
        product_name: looseProduct.name,
        quantity: quantity,
        unit_price: unitPrice,
        total_price: totalPrice,
        mrp: unitPrice,
        discounted_price: 0,
        measurement: quantity,
        unit_name: looseProduct.unitName,
        unit_short_code: looseProduct.unit
      }]);

      // Reset form and close dialog
      const defaultUnit = getDefaultUnit();
      setLooseProduct({ 
        name: '', 
        price: '', 
        quantity: 1, 
        unit: defaultUnit.short_code, 
        unitName: defaultUnit.name,
        pricingMethod: 'per_unit'
      });
      setLooseProductDialog(false);
      
      setSnackbar({
        open: true,
        message: 'Loose item added to cart successfully!',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields for the loose item',
        severity: 'error'
      });
    }
  };

  const addAdditionalCharge = () => {
    if (newCharge.name && newCharge.amount) {
      setAdditionalCharges([...additionalCharges, {
        charge_name: newCharge.name,
        amount: parseFloat(newCharge.amount)
      }]);
      setNewCharge({ name: '', amount: '' });
    }
  };

  const removeAdditionalCharge = (index) => {
    setAdditionalCharges(additionalCharges.filter((_, i) => i !== index));
  };

  const calculateTotalMRP = () => {
    return cart.reduce((sum, item) => sum + (item.mrp * item.quantity), 0);
  };

  const calculateItemDiscount = () => {
    const totalMRP = calculateTotalMRP();
    const totalSalePrice = cart.reduce((sum, item) => {
      const salePrice = item.discounted_price > 0 ? item.discounted_price : item.mrp;
      return sum + (salePrice * item.quantity);
    }, 0);
    return totalMRP - totalSalePrice;
  };

  const calculateSaleSubtotal = () => {
    return cart.reduce((sum, item) => {
      const salePrice = item.discounted_price > 0 ? item.discounted_price : item.mrp;
      return sum + (salePrice * item.quantity);
    }, 0);
  };

  const calculateAdditionalChargesTotal = () => {
    return additionalCharges.reduce((sum, charge) => sum + Number(charge.amount), 0);
  };

  const calculateFinalPayable = () => {
    const saleSubtotal = calculateSaleSubtotal();
    const charges = calculateAdditionalChargesTotal();
    return saleSubtotal + charges;
  };

  const resetSaleState = () => {
    setCart([]);
    setAdditionalCharges([]);
    setMultiplePayments([]);
    setShowMultiplePayments(false);
    setPaymentMethod('cash');
    setCustomer({ name: '', phone: '', email: '' });
    setSelectedCustomer(null);
    setNewCharge({ name: '', amount: '' });
    setCurrentOrderId(null);
  };

  const getDefaultHoldName = () => `Hold ${new Date().toLocaleString()}`;

  const handleOpenHoldDialog = () => {
    if (!cart || cart.length === 0) {
      setSnackbar({ open: true, message: 'Add items to cart before holding a bill.', severity: 'warning' });
      return;
    }
    setHoldName(getDefaultHoldName());
    setHoldDialogOpen(true);
  };

  const handleSaveHoldBill = () => {
    if (!cart || cart.length === 0) {
      setSnackbar({ open: true, message: 'No items to hold.', severity: 'warning' });
      return;
    }

    const bill = {
      id: Date.now().toString(),
      name: (holdName && holdName.trim()) || getDefaultHoldName(),
      createdAt: new Date().toISOString(),
      cart,
      additionalCharges,
      customer,
      selectedCustomer,
      paymentMethod,
      multiplePayments,
      showMultiplePayments,
      totals: {
        itemCount: cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
        payable: calculateFinalPayable(),
      },
    };

    addHeldBill(bill);
    setHeldBills(loadHeldBills());
    resetSaleState();
    setHoldDialogOpen(false);
    setHoldName('');
    setSnackbar({ open: true, message: 'Bill held successfully.', severity: 'success' });
  };

  // Single source of truth for the payload sent to the POS order API. Loose
  // items travel with null product/variant ids plus an explicit flag, so they
  // are stored as ad-hoc lines instead of being looked up as variants.
  const buildOrderItems = () => cart.map(item => ({
    product_id: item.product_id ?? null,
    product_variant_id: item.product_variant_id ?? null,
    is_loose_item: !!item.is_loose_item,
    product_name: item.product_name,
    quantity: item.quantity,
    unit_price: item.unit_price,
    total_price: item.total_price,
    mrp: item.mrp,
    unit_name: item.unit_name,
    unit_short_code: item.unit_short_code,
    measurement: item.measurement
  }));

  const prepareOrderData = () => {
    // Calculate total discount (only item-level discount now)
    const totalMRP = calculateTotalMRP();
    const itemDiscount = calculateItemDiscount();
    const totalDiscountPercentage = totalMRP > 0 ? (itemDiscount / totalMRP) * 100 : 0;

    return {
      customer: selectedCustomer || (customer.name ? customer : null),
      items: buildOrderItems(),
      additional_charges: additionalCharges,
      discount_percentage: totalDiscountPercentage,
      discount_amount: itemDiscount, // Only item discount amount
      payment_method: getPaymentMethodString()
    };
  };

  // Print only the bill in a new window (thermal-friendly)
  const printPOSBill = async () => {
    if (!cart || cart.length === 0) {
      alert('No items to print');
      return;
    }

    // Ensure we have seller info for the print (some flows may not have loaded it yet)
    let store = sellerInfo;
    if (!store) {
      try {
        const res = await getSellerInfo();
        store = res.data;
      } catch (e) {
        store = null;
      }
    }

    const generateBillHTML = () => {
      const items = cart || [];
      const totalAmount = calculateTotalMRP();
      const subtotal = calculateSaleSubtotal();
      const discountAmount = calculateItemDiscount();
      const finalTotal = calculateFinalPayable();

      return `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Bill</title>
            <meta charset="UTF-8">
            <style>
              *{margin:0;padding:0;box-sizing:border-box}
              body{font-family:'Helvetica Neue','Helvetica','Arial',sans-serif;background:#fff;color:#000;font-size:13px;line-height:1.18}
              .invoice-container{width:100%;padding:0;margin:0}
              .invoice-container *{color:#000 !important;background:transparent !important}
              @media print{ @page{margin:0} body{font-size:12px} }
            </style>
          </head>
          <body>
            <div class="invoice-container">
               <!-- Header -->
            <div style="text-align: center; margin: 0 0 4px 0; padding: 2px 0; border-bottom: 2px solid black; width: 100%;">
              <div style="font-size: 20px; margin: 0 0 2px 0; letter-spacing: 0.5px; text-transform: none; padding: 0; font-weight:700; font-family: 'Brush Script MT', 'Lucida Handwriting', 'Dancing Script', cursive; font-style: italic;">
                ${sellerInfo?.store_name || sellerInfo?.name || 'Store Name'}
              </div>
              ${sellerInfo?.formatted_address ? `<div style="font-size:12px;margin:0 0 1px 0;padding:0;">${sellerInfo.formatted_address}</div>` : ''}
              ${sellerInfo?.email || sellerInfo?.mobile ? `<div style="font-size:12px;margin:0 0 1px 0;padding:0;">${sellerInfo.email ? '' + sellerInfo.email : ''}${sellerInfo.mobile ? ' | ' + sellerInfo.mobile : ''}</div>` : ''}
            </div>
            <!-- Order Info -->
              <div style="font-size:11px;margin-bottom:6px;border-bottom:1px dashed #000;padding-bottom:4px;">
                <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:2px;">
                  <div>Date: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
                  <div style="text-align:right;">Bill No: #${currentOrderId || ''}</div>
                </div>
                ${selectedCustomer ? `<div style="font-size:11px;margin-top:2px;">Customer: ${selectedCustomer.name}</div>` : ''}
                <div style="font-size:11px;margin-top:2px;">Payment: ${getPaymentMethodString().toUpperCase()}</div>
              </div>

              <div style="width:100%;font-size:11px;border-bottom:1px solid #000;padding-bottom:4px;display:flex;gap:6px;">
                <div style="flex:0.5;text-align:center;">SNo</div>
                <div style="flex:2;">Item</div>
                <div style="flex:1;text-align:center;">MRP</div>
                <div style="flex:0.8;text-align:center;">Disc%</div>
                <div style="flex:0.8;text-align:center;">Qty</div>
                <div style="flex:1;text-align:right;">Total</div>
              </div>

              ${items.map((item, index) => {
                const mrp = Number(item.mrp || 0);
                const salePrice = item.discounted_price > 0 ? Number(item.discounted_price) : mrp;
                const quantity = Number(item.quantity || 1);
                const totalPrice = salePrice * quantity;
                const totalMRP = mrp * quantity;
                const discountAmount = totalMRP - totalPrice;
                const discountPercent = totalMRP > 0 ? Math.round((discountAmount / totalMRP) * 100) : 0;
                const discountedRate = mrp - (mrp * discountPercent / 100);

                return `
                  <div style="display:flex;gap:6px;padding:3px 0;align-items:flex-start;font-size:11px;">
                    <div style="flex:0.5;text-align:center;">${index + 1}</div>
                    <div style="flex:2;">${item.product_name}${discountPercent > 0 ? `<div style="font-size:10px;margin-top:2px;">@ ₹${discountedRate.toFixed(2)}/${item.unit_short_code || 'pc'}</div>` : ''}</div>
                    <div style="flex:1;text-align:center;">₹${mrp.toFixed(2)}</div>
                    <div style="flex:0.8;text-align:center;">${discountPercent}%</div>
                    <div style="flex:0.8;text-align:center;">${quantity} ${item.unit_short_code || ''}</div>
                    <div style="flex:1;text-align:right;">₹${totalPrice.toFixed(2)}</div>
                  </div>
                `;
              }).join('')}

              <div style="border-top:1px dashed #000;margin-top:6px;padding-top:6px;">
                <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:4px;">
                  <div>Total Amount:</div>
                  <div>₹${totalAmount.toFixed(2)}</div>
                </div>
                ${discountAmount > 0 ? `
                  <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:4px;">
                    <div>Discount:</div>
                    <div>-₹${discountAmount.toFixed(2)}</div>
                  </div>
                ` : ''}
                <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:4px;">
                  <div>Subtotal:</div>
                  <div>₹${subtotal.toFixed(2)}</div>
                </div>
                ${additionalCharges && additionalCharges.map(charge => `
                  <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:4px;">
                    <div>${charge.charge_name}:</div>
                    <div>₹${Number(charge.amount).toFixed(2)}</div>
                  </div>
                `).join('')}

                <div style="border-top:1px dashed #000;padding-top:6px;display:flex;justify-content:space-between;font-size:12px;font-weight:700;">
                  <div>Final Payable:</div>
                  <div>₹${finalTotal.toFixed(2)}</div>
                </div>
              </div>

              <div style="text-align:center;padding-top:8px;border-top:2px solid #000;margin-top:8px;">
                <div style="font-size:14px;margin-bottom:4px;font-weight:700;">*** THANK YOU ***</div>
                <div style="font-size:12px;margin-bottom:4px;">Visit Again Soon</div>
                ${qrCodeDataUrl ? `<div style="margin:6px 0;"><img src="${qrCodeDataUrl}" style="width:60px;height:60px;object-fit:contain;border:0;"/></div>` : ''}
                <div style="font-size: 10px; margin: 0 0 1px 0; padding: 0;">Scan for Mobile App</div>
                <div style="font-size: 10px; margin: 0 0 8px 0; padding: 0;">Order Online</div>
                <hr>
                <div style="font-size: 8px; margin: 0 0 8px 0; padding: 0;">Powered by : OwnTrip (OPC) Pvt. Ltd.</div>
                <hr>
                <div style="margin-top:8px;">&nbsp;</div>
              </div>
            </div>
            <script>
              window.onload = function(){ setTimeout(function(){ window.print(); window.close(); }, 250); }
            </script>
          </body>
        </html>
      `;
    };

    const printWindow = window.open('', '_blank', 'width=400,height=600,scrollbars=yes');
    printWindow.document.write(generateBillHTML());
    printWindow.document.close();

    // wait a bit for print window to open and print
    return new Promise(resolve => setTimeout(resolve, 800));
  };

  const handleBarcodeScan = useCallback(async (barcode) => {
    
    if (!barcode || !barcode.trim()) {
      return;
    }
    
    try {
      const response = await searchProductByBarcode(barcode);
      const product = response.data;
      
      if (product.variants && product.variants.length > 1) {
        // Product has multiple variants, show selection dialog
        setScannedProduct(product);
        setVariantDialog(true);
      } else if (product.variants && product.variants.length === 1) {
        // Product has one variant, add it directly
        addToCart(product.variants[0], product);
        setSnackbar({
          open: true,
          message: `${product.name} added to cart`,
          severity: 'success'
        });
      } else {
        // Product has no variants, add the base product
        addToCart(product);
        setSnackbar({
          open: true,
          message: `${product.name} added to cart`,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error scanning barcode:', error);
      setSnackbar({
        open: true,
        message: 'Product not found with this barcode',
        severity: 'error'
      });
    }
  }, [addToCart]);

  const handleVariantSelect = (variant) => {
    addToCart(variant, scannedProduct);
    setVariantDialog(false);
    setScannedProduct(null);
    setSnackbar({
      open: true,
      message: `${scannedProduct?.name} (${variant.type}) added to cart`,
      severity: 'success'
    });
  };

  const handleSaveCustomer = async () => {
    if (customer.name && customer.phone) {
      try {
        const response = await createCustomer({
          name: customer.name,
          phone: customer.phone,
          email: customer.email || undefined
        });
        
        console.log('Customer creation response:', response);
        
        // Extract customer data from API response
        const savedCustomer = response.data || response;
        console.log('Saved customer data:', savedCustomer);
        
        if (savedCustomer && savedCustomer.id) {
          setSelectedCustomer(savedCustomer);
          setSnackbar({
            open: true,
            message: 'Customer saved and selected successfully!',
            severity: 'success'
          });
          setCustomerDialog(false);
        } else {
          throw new Error('Customer creation failed - no ID returned');
        }
      } catch (error) {
        console.error('Error saving customer:', error);
        setSnackbar({
          open: true,
          message: 'Error saving customer',
          severity: 'error'
        });
      }
    }
  };

  // Global barcode scanner input capture
  useEffect(() => {
    let barcodeBuffer = '';
    let lastKeyTime = 0;
    const BARCODE_TIMEOUT = 100; // ms between keystrokes for barcode input

    const handleKeyDown = (e) => {
      // Skip barcode scanning if user is typing in an input field
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.contentEditable === 'true')) {
        return; // Let normal input handling occur
      }
      
      const currentTime = Date.now();
      
      // If it's been more than BARCODE_TIMEOUT since last key, start new barcode
      if (currentTime - lastKeyTime > BARCODE_TIMEOUT) {
        barcodeBuffer = '';
      }
      
      // Only capture alphanumeric characters and some special chars for barcodes
      if (e.key.length === 1 || (e.key === 'Enter' && barcodeBuffer.length > 0)) {
        if (e.key === 'Enter') {
          // Process the complete barcode
          if (barcodeBuffer.trim()) {
            handleBarcodeScan(barcodeBuffer.trim());
          }
          barcodeBuffer = '';
        } else {
          barcodeBuffer += e.key;
        }
        lastKeyTime = currentTime;
        
        // Prevent default behavior for barcode input
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Add global event listener
    document.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleBarcodeScan]);

  const handlePayment = async (printBill = false) => {
    try {
      // Calculate total discount (only item-level discount now)
      const totalMRP = calculateTotalMRP();
      const itemDiscount = calculateItemDiscount();
      const totalDiscountPercentage = totalMRP > 0 ? (itemDiscount / totalMRP) * 100 : 0;

      // Prepare order data
      const orderData = {
        customer: selectedCustomer || (customer.name ? customer : null),
        items: buildOrderItems(),
        additional_charges: additionalCharges,
        discount_percentage: totalDiscountPercentage,
        discount_amount: itemDiscount, // Only item discount amount
        payment_method: getPaymentMethodString(),
        print_bill: printBill
      };

      // Create the POS order
      const response = await createPOSOrder(orderData);

      setCurrentOrderId(response.order.billNumber || response.order.id);

      setSnackbar({
        open: true,
        message: `Order #${response.order.id} created successfully${printBill ? ' and bill printed' : ''}!`,
        severity: 'success'
      });

      // Update stock locally without reloading all products
      updateProductStockLocally(cart);

      // Reset cart and forms
      setCart([]);
      setAdditionalCharges([]);
      setCustomer({ name: '', phone: '', email: '' });
      setPaymentConfirmationDialog(false);
      setSelectedCustomer(null);
      setMultiplePayments([]);
      setShowMultiplePayments(false);
      setPaymentMethod('cash');

      // Open bill dialog if requested
      if (printBill) {
        setBillPrintDialog(true);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error processing order',
        severity: 'error'
      });
    }
  };

  return (
    <Box className="pos-page" sx={{ p: 1, backgroundColor: '#f8f9fa', height: '100%' }}>
      <Box className="pos-workspace" sx={{ 
        display: 'grid', 
        gridTemplateColumns: '3fr 2fr', 
        gap: 2, 
        px: 1, 
        height: '100%', 
        '@media (max-width: 900px)': { gridTemplateColumns: '1fr' } 
      }}>
        {/* Products Section - 60% */}
        <Box className="pos-catalog-column">
          <Paper sx={{ 
            p: 1.5, 
            pb: 1,
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }} className="pos-catalog-panel">
            <Box className="pos-panel-header">
              <Box>
                {viewMode === 'products' && !searchTerm && (
                  <Button
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackToCategories}
                    sx={{ mr: 1, mb: 0.5 }}
                  >
                    Back to Categories
                  </Button>
                )}
                <Typography className="pos-panel-eyebrow">
                  {searchTerm ? 'Search results' : viewMode === 'categories' ? 'Browse categories' : selectedCategory?.name || 'Products'}
                </Typography>
                <Typography className="pos-panel-title">
                  {searchTerm ? `Found ${filteredProducts.length} products` : viewMode === 'categories' ? 'Select a category' : `${filteredProducts.length} products`}
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search by name, barcode, brand, type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ 
                  width: '400px',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '12px',
                    backgroundColor: '#f8f9fa',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#3498db',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3498db',
                    },
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: '#7f8c8d', mr: 1, fontSize: '1.1rem' }} />
                  ),
                }}
              />
            </Box>

            <Box className="pos-product-scroll"
              ref={containerRef}
              sx={{ flexGrow: 1, overflow: 'auto' }}
            >
              {/* Category Grid View */}
              {viewMode === 'categories' && !searchTerm && (
                <Box className="pos-category-grid" sx={{ position: 'relative', minHeight: '200px' }}>
                  {isLoadingCategories ? (
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={36} sx={{ color: '#667eea' }} />
                      <Typography sx={{ color: '#7f8c8d', fontSize: '0.85rem', fontWeight: 600 }}>Loading categories...</Typography>
                    </Box>
                  ) : categories.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', py: 4, color: '#7f8c8d' }}>No categories with products found</Typography>
                  ) : (
                    categories.map(category => (
                      <Card
                        key={category.id}
                        className="pos-category-card"
                        onClick={() => handleCategoryClick(category)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                          },
                          overflow: 'hidden',
                          borderRadius: '12px',
                          border: 'none',
                          boxShadow: 'none',
                          background: 'transparent'
                        }}
                      >
                        <Box sx={{ height: '100px', backgroundColor: '#fafafa', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.8 }}>
                          {category.image ? (
                            <img
                              src={`https://martadmin.owntrip.in/storage/${category.image}`}
                              alt={category.name}
                              style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '6px', borderRadius: '12px' }}
                            />
                          ) : category.web_image ? (
                            <img
                              src={`https://martadmin.owntrip.in/storage/${category.web_image}`}
                              alt={category.name}
                              style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '6px', borderRadius: '12px' }}
                            />
                          ) : (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <CategoryIcon sx={{ fontSize: 48, color: '#bdc3c7' }} />
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 0.2 }}>
                            {category.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#95a5a6', fontWeight: '600' }}>
                            {category.product_count} {category.product_count === 1 ? 'product' : 'products'}
                          </Typography>
                        </Box>
                      </Card>
                    ))
                  )}
                </Box>
              )}

              {/* Search Results with Category Grouping */}
              {searchTerm && filteredProducts.length > 0 && Object.keys(productsByCategory).length > 1 ? (
                <Box sx={{ pb: 2 }}>
                  {Object.entries(productsByCategory).map(([categoryName, categoryProducts]) => (
                    <Box key={categoryName} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#2c3e50', px: 1 }}>
                        {categoryName} ({categoryProducts.length})
                      </Typography>
                      <Grid container spacing={1} sx={{ justifyContent: 'flex-start' }}>
                        {categoryProducts.slice(0, 12).map(product => (
                          <Grid item key={product.id} sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                            {/* Product card (reuse existing) */}
                            <Card className="pos-product-card" sx={{
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              width: '110px',
                              height: '160px',
                              minWidth: '110px',
                              maxWidth: '110px',
                              borderRadius: '12px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              border: '1px solid #e0e0e0',
                              overflow: 'hidden'
                            }}>
                            <Box sx={{ position: 'relative' }}>
                              <CardMedia
                                component="img"
                                height="70"
                                image={`https://martadmin.owntrip.in/storage/${product.image}`}
                                alt={product.name}
                                sx={{ objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                              />
                              {!product.variants?.some(v => v.stock > 0) ? (
                                <Box sx={{ position: 'absolute', bottom: '4px', right: '4px', width: '24px', height: '24px', borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 2px 8px rgba(239,68,68,0.4)' }}>
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </Box>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={() => addToCart(product)}
                                  sx={{
                                    position: 'absolute',
                                    bottom: '4px',
                                    right: '4px',
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #10b981, #14b8a6)',
                                    color: '#fff',
                                    padding: 0,
                                    boxShadow: '0 2px 8px rgba(16,185,129,0.4)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #059669, #0d9488)',
                                      boxShadow: '0 4px 16px rgba(16,185,129,0.6)',
                                      transform: 'scale(1.15)',
                                      '& .MuiSvgIcon-root': { transform: 'scale(1.2)' },
                                    },
                                    '&:active': {
                                      transform: 'scale(0.95)',
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: 14 }
                                  }}
                                >
                                  <AddIcon />
                                </IconButton>
                              )}
                            </Box>
                      <CardContent sx={{ p: 0.8, height: 'auto', minHeight: '90px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" sx={{ fontWeight: '400', fontSize: '0.55rem', lineHeight: 1.1, color: '#2c3e50', mb: 0.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {product.name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#e74c3c', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                ₹{product.variants?.[0]?.discounted_price || product.variants?.[0]?.price || 'N/A'}
                              </Typography>
                            </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              ) : (
                <>
                {/* Regular Product Grid (category view or single category) */}
                <Grid container spacing={1} sx={{ pb: 2, justifyContent: 'flex-start' }}>
                  {displayedProducts.map(product => (
                  <Grid item xs={12} sm={6} md={2} lg={2} key={product.id} sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    <Card className="pos-product-card" sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mt: 0.5,
                      '&:hover': { 
                        transform: 'translateY(-4px) scale(1.02)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        zIndex: 10
                      },
                      width: '110px',
                      height: '160px',
                      minWidth: '110px',
                      maxWidth: '110px',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      border: '1px solid #e0e0e0',
                      overflow: 'hidden'
                    }}>
                      <Box sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="70"
                          image={`https://martadmin.owntrip.in/storage/${product.image}`}
                          alt={product.name}
                          onClick={() => { setSelectedProductDetail(product); setProductDetailDialog(true); }}
                          sx={{ 
                            objectFit: 'contain', 
                            backgroundColor: '#f8f9fa',
                            borderBottom: '1px solid #e0e0e0',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            }
                          }}
                        />
                        {!product.variants?.some(variant => variant.stock > 0) ? (
                          <Box sx={{ position: 'absolute', bottom: '4px', right: '4px', width: '24px', height: '24px', borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 2px 8px rgba(239,68,68,0.4)' }}>
                            <CloseIcon sx={{ fontSize: 14, transition: 'transform 0.3s ease' }} />
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (product.variants && product.variants.length > 1) {
                                setScannedProduct(product);
                                setVariantDialog(true);
                              } else {
                                addToCart(product);
                              }
                            }}
                            sx={{
                              position: 'absolute',
                              bottom: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #10b981, #14b8a6)',
                              color: '#fff',
                              padding: 0,
                              boxShadow: '0 2px 8px rgba(16,185,129,0.4)',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #059669, #0d9488)',
                                boxShadow: '0 4px 16px rgba(16,185,129,0.6)',
                                transform: 'scale(1.15)',
                                '& .MuiSvgIcon-root': {
                                  transform: 'scale(1.2)',
                                },
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  width: '100%',
                                  height: '100%',
                                  borderRadius: '50%',
                                  border: '2px solid rgba(16,185,129,0.5)',
                                  transform: 'translate(-50%, -50%) scale(1)',
                                  animation: 'plusRipple 0.6s ease-out',
                                },
                              },
                              '&:active': {
                                transform: 'scale(0.95)',
                              },
                              '& .MuiSvgIcon-root': { fontSize: 14, transition: 'transform 0.3s ease' }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        )}
                      </Box>
                      <CardContent sx={{ p: 0.8, height: '90px', display: 'flex', flexDirection: 'column' }}>
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: '400', 
                              fontSize: '0.55rem', 
                              lineHeight: 1.1,
                              color: '#2c3e50',
                              mb: 0.3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {product.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {product.variants?.[0]?.discounted_price > 0 ? (
                              <>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#7f8c8d', 
                                    fontSize: '0.6rem',
                                    textDecoration: 'line-through'
                                  }}
                                >
                                  ₹{product.variants?.[0]?.price}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#e74c3c', 
                                    fontWeight: 'bold', 
                                    fontSize: '0.7rem' 
                                  }}
                                >
                                  ₹{product.variants?.[0]?.discounted_price}
                                </Typography>
                              </>
                            ) : (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#e74c3c', 
                                  fontWeight: 'bold', 
                                  fontSize: '0.7rem' 
                                }}
                              >
                                ₹{product.variants?.[0]?.price ?? 'N/A'}
                              </Typography>
                            )}
                          </Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#7f8c8d', 
                              fontSize: '0.6rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {product.variants?.[0]?.measurement} {product.variants?.[0]?.unit_short_code || product.variants?.[0]?.unit_name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                          {product.indicator !== undefined && product.indicator !== null && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontSize: '0.5rem',
                                fontWeight: 'bold',
                                color: Number(product.indicator) === 1 ? '#27ae60' : Number(product.indicator) === 2 ? '#e74c3c' : '#95a5a6',
                                bgcolor: Number(product.indicator) === 1 ? '#d5f4e6' : Number(product.indicator) === 2 ? '#fadbd8' : '#f8f9fa',
                                px: 0.4,
                                py: 0.2,
                                borderRadius: '3px',
                                lineHeight: 1,
                                mr: 0.5
                              }}
                            >
                              {Number(product.indicator) === 1 ? 'VEG' : Number(product.indicator) === 2 ? 'NON-VEG' : 'N/A'}
                            </Typography>
                          )}
                          {product.type && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#7f8c8d',
                                fontSize: '0.55rem',
                                fontWeight: '500',
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mx: 0.5
                              }}
                            >
                              {product.type}
                            </Typography>
                          )}
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: product.variants?.[0]?.stock > 0 ? '#27ae60' : '#e74c3c',
                              fontSize: '0.55rem',
                              fontWeight: '500',
                              ml: 'auto'
                            }}
                          >
                            {product.variants?.[0]?.stock ?? 0}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {searchTerm && filteredProducts.length === 0 && (
                <Typography sx={{ textAlign: 'center', py: 4, color: '#7f8c8d' }}>
                  No products found for "{searchTerm}"
                </Typography>
              )}
              {!searchTerm && viewMode === 'products' && filteredProducts.length === 0 && (
                <Typography sx={{ textAlign: 'center', py: 4, color: '#7f8c8d' }}>
                  No products in this category
                </Typography>
              )}
              <Pagination
                count={Math.ceil(filteredProducts.length / itemsPerPage)}
                page={currentPage}
                onChange={(event, page) => setCurrentPage(page)}
                sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}
              />
            </>
            )}
            </Box>
          </Paper>
        </Box>

        {/* Cart Section - 40% */}
        <Box className="pos-cart-column">
          <Paper sx={{ 
            p: 0.5, 
            pb: 0.5,
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }} className="pos-cart-panel"
          ref={cartContainerRef}
          >
            <Box className="pos-cart-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                color: '#2c3e50'
              }}>
                <CartIcon sx={{ mr: 1, color: '#3498db' }} />
                Cart ({cart.length} items)
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ position: 'relative', maxWidth: '250px' }}>
                  <TextField
                    size="small"
                    placeholder="Search customers..."
                    value={customerSearchTerm}
                    onChange={(e) => {
                      setCustomerSearchTerm(e.target.value);
                    }}
                    onFocus={() => {
                      if (customerSearchResults.length > 0) {
                        setShowCustomerDropdown(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowCustomerDropdown(false), 200);
                    }}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: '#7f8c8d', mr: 1, fontSize: '1rem' }} />,
                    }}
                    sx={{
                      width: '200px',
                      '& .MuiOutlinedInput-root': {
                        height: '32px',
                        borderRadius: '20px',
                        backgroundColor: 'white',
                        fontSize: '0.8rem',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#3498db',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#3498db',
                        },
                      },
                    }}
                  />
                  {showCustomerDropdown && customerSearchResults.length > 0 && (
                    <Paper
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        maxHeight: '150px',
                        overflow: 'auto',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        mt: 0.5
                      }}
                    >
                      {customerSearchResults.map((customer) => (
                        <Box
                          key={customer.id}
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setCustomerSearchTerm('');
                            setCustomerSearchResults([]);
                            setShowCustomerDropdown(false);
                          }}
                          sx={{
                            p: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: '#f8f9fa'
                            },
                            borderBottom: '1px solid #e0e0e0',
                            '&:last-child': {
                              borderBottom: 'none'
                            }
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: '500', color: '#2c3e50', fontSize: '0.8rem' }}>
                            {customer.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#7f8c8d', fontSize: '0.7rem' }}>
                            {customer.phone}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>
                <IconButton
                  onClick={() => {
                    setCustomer({ name: '', phone: '', email: '' });
                    setCustomerDialog(true);
                  }}
                  sx={{
                    bgcolor: '#3498db',
                    color: 'white',
                    '&:hover': { bgcolor: '#2980b9' },
                    width: '32px',
                    height: '32px'
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
                <IconButton
                  onClick={() => {
                    setHeldBills(loadHeldBills());
                    setHeldBillsDialog(true);
                  }}
                  sx={{
                    bgcolor: '#f39c12',
                    color: 'white',
                    '&:hover': { bgcolor: '#e67e22' },
                    width: '32px',
                    height: '32px',
                    position: 'relative'
                  }}
                >
                  <ListIcon sx={{ fontSize: '1rem' }} />
                  {heldBills.length > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        bgcolor: '#e74c3c',
                        color: 'white',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.6rem',
                        fontWeight: 'bold',
                        border: '1px solid white'
                      }}
                    >
                      {heldBills.length}
                    </Box>
                  )}
                </IconButton>
              </Box>
            </Box>

            {/* Cart Items */}
            <Box sx={{ 
              height: `${cartItemsHeight}px`,
              overflow: 'auto', 
              mb: 0.5, 
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '3px',
                '&:hover': {
                  backgroundColor: '#a8a8a8',
                },
              },
            }}>
              {cart.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 3,
                  color: '#7f8c8d'
                }}>
                  <CartIcon sx={{ fontSize: '3rem', color: '#bdc3c7', mb: 1 }} />
                  <Typography color="text.secondary">
                    Your cart is empty
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ 
                  bgcolor: 'white', 
                  borderRadius: '8px',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  {cart.map(item => (
                    <ListItem key={item.line_id} sx={{ px: 0.2, py: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: '500',
                                color: '#2c3e50',
                                fontSize: '0.75rem',
                                lineHeight: 1.1,
                                mb: 0,
                                // The name must only take the width it needs, so the
                                // "Loose" chip sits next to it instead of being pushed
                                // to the far right where the qty/delete buttons are.
                                minWidth: 0,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {item.product_name}
                            </Typography>
                            {item.is_loose_item && (
                              <Chip 
                                label="Loose Items" 
                                size="small" 
                                sx={{ 
                                  // styles.css pins chips via
                                  //   .enterprise-content .MuiChip-root {
                                  //     height: 24px !important; font-size: .7rem !important; }
                                  // so the compact badge size has to win with !important
                                  // too (same approach as the other chip overrides in that file).
                                  '&.MuiChip-sizeSmall': {
                                    height: '14px !important',
                                    fontSize: '0.5rem !important',
                                    borderRadius: '3px'
                                  },
                                  fontWeight: 600,
                                  bgcolor: '#ff9800', 
                                  color: 'white',
                                  flexShrink: 0,
                                  '& .MuiChip-label': { px: 0.4, lineHeight: '14px', whiteSpace: 'nowrap' }
                                }} 
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" sx={{ color: '#7f8c8d', fontSize: '0.65rem', lineHeight: 1, mt: 0 }}>
                              ₹{item.unit_price} per {item.unit_short_code} × {item.quantity} {item.unit_short_code}
                            </Typography>
                            {item.measurement && !item.is_loose_item && (
                              <Typography variant="caption" sx={{ color: '#95a5a6', fontSize: '0.6rem', lineHeight: 1 }}>
                                {item.measurement} {item.unit_short_code || item.unit_name}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              const stepSize = item.is_loose_item && ['gm', 'mg', 'ml', 'cl'].includes(item.unit_short_code) ? 0.1 :
                                             item.is_loose_item && ['kg', 'ltr', 'l', 'ton', 'qtl'].includes(item.unit_short_code) ? 0.1 : 
                                             item.is_loose_item ? 0.01 : 1;
                              updateQuantity(item.line_id, Math.max(stepSize, item.quantity - stepSize));
                            }}
                            sx={{ 
                              bgcolor: '#ecf0f1',
                              '&:hover': { bgcolor: '#d5dbdb' },
                              width: '20px',
                              height: '20px',
                              padding: '2px',
                              minWidth: '20px'
                            }}
                          >
                            <RemoveIcon sx={{ fontSize: '12px' }} />
                          </IconButton>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              minWidth: '24px', 
                              textAlign: 'center',
                              fontWeight: 'bold',
                              color: '#2c3e50',
                              fontSize: '0.7rem'
                            }}
                          >
                            {item.is_loose_item ? item.quantity : Math.round(item.quantity)}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              const stepSize = item.is_loose_item && ['gm', 'mg', 'ml', 'cl'].includes(item.unit_short_code) ? 0.1 :
                                             item.is_loose_item && ['kg', 'ltr', 'l', 'ton', 'qtl'].includes(item.unit_short_code) ? 0.1 : 
                                             item.is_loose_item ? 0.01 : 1;
                              updateQuantity(item.line_id, item.quantity + stepSize);
                            }}
                            sx={{ 
                              bgcolor: '#ecf0f1',
                              '&:hover': { bgcolor: '#d5dbdb' },
                              width: '20px',
                              height: '20px',
                              padding: '2px',
                              minWidth: '20px'
                            }}
                          >
                            <AddIcon sx={{ fontSize: '12px' }} />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => removeFromCart(item.line_id)}
                            sx={{ 
                              bgcolor: '#fee',
                              '&:hover': { bgcolor: '#fdd' },
                              width: '20px',
                              height: '20px',
                              padding: '2px',
                              minWidth: '20px'
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: '12px' }} />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {selectedCustomer && (
              <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: '#2c3e50', fontSize: '0.8rem' }}>
                  Customer: <span style={{ fontWeight: 'bold', color: '#27ae60' }}>{selectedCustomer.name} ({selectedCustomer.phone})</span>
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setSelectedCustomer(null)}
                  sx={{ 
                    width: '20px', 
                    height: '20px', 
                    padding: '2px',
                    color: '#95a5a6',
                    '&:hover': { color: '#e74c3c' }
                  }}
                >
                  <CloseIcon sx={{ fontSize: '14px' }} />
                </IconButton>
              </Box>
            )}

            <Divider sx={{ my: 0.5 }} />

            {/* Add Loose Items */}
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setLooseProductDialog(true)}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  color: '#3498db',
                  '&:hover': {
                    backgroundColor: 'rgba(52, 152, 219, 0.1)'
                  }
                }}
              >
                + Add Loose Items
              </Button>
            </Box>

            {/* Additional Charges */}
            <Box sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                Additional Charges
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Charge name"
                  value={newCharge.name}
                  onChange={(e) => setNewCharge({ ...newCharge, name: e.target.value })}
                  sx={{ 
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      height: '32px'
                    }
                  }}
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder="Amount"
                  value={newCharge.amount}
                  onChange={(e) => setNewCharge({ ...newCharge, amount: e.target.value })}
                  sx={{ 
                    width: '120px',
                    '& .MuiOutlinedInput-root': {
                      height: '32px'
                    }
                  }}
                />
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={addAdditionalCharge}
                  sx={{
                    height: '32px',
                    minWidth: 'auto'
                  }}
                >
                  Add
                </Button>
              </Box>
              {additionalCharges.map((charge, index) => (
                <Chip
                  key={index}
                  label={`${charge.charge_name}: ₹${charge.amount}`}
                  onDelete={() => removeAdditionalCharge(index)}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>

            {/* Totals */}
            <Box sx={{ 
              mb: 1.5, 
              p: 1, 
              bgcolor: 'white', 
              borderRadius: '4px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontSize: '0.75rem' }}>Total Amount:</Typography>
                <Typography variant="caption" sx={{ fontWeight: '500', fontSize: '0.75rem' }}>₹{calculateTotalMRP().toFixed(2)}</Typography>
              </Box>

              {calculateItemDiscount() > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontSize: '0.75rem' }}>
                    Discount ({calculateTotalMRP() > 0 ? ((calculateItemDiscount() / calculateTotalMRP()) * 100).toFixed(0) : 0}%):
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: '500', color: '#e74c3c', fontSize: '0.75rem' }}>-₹{calculateItemDiscount().toFixed(2)}</Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#7f8c8d', fontSize: '0.75rem' }}>Subtotal:</Typography>
                <Typography variant="caption" sx={{ fontWeight: '500', fontSize: '0.75rem' }}>₹{calculateSaleSubtotal().toFixed(2)}</Typography>
              </Box>

              {additionalCharges.map((charge, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#7f8c8d', fontSize: '0.75rem' }}>{charge.charge_name}:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: '500', color: '#e67e22', fontSize: '0.75rem' }}>₹{Number(charge.amount).toFixed(2)}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 0.5, borderColor: '#ecf0f1' }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '0.8rem' }}>Total:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e74c3c', fontSize: '0.8rem' }}>₹{calculateFinalPayable().toFixed(2)}</Typography>
              </Box>
            </Box>

            {/* Payment Methods */}
            <Box sx={{ 
              mb: 1.5, 
              p: 1, 
              bgcolor: 'white', 
              borderRadius: '4px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
            }}>
              <Typography variant="caption" sx={{ color: '#7f8c8d', fontSize: '0.75rem', mb: 1, display: 'block' }}>
                Payment Method
              </Typography>
              
              {/* Payment Method Row with Pay Now Button */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <FormControl size="small" sx={{ 
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: '32px'
                  }
                }}>
                  <Select
                    value={multiplePayments.length > 0 ? 'multiple' : paymentMethod}
                    onChange={(e) => {
                      if (e.target.value === 'multiple') {
                        setShowMultiplePayments(true);
                      } else {
                        setPaymentMethod(e.target.value);
                        setShowMultiplePayments(false);
                        setMultiplePayments([]);
                      }
                    }}
                    sx={{
                      borderRadius: '6px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontSize: '0.8rem'
                      }
                    }}
                  >
                    <MenuItem value="cash" sx={{ fontSize: '0.8rem' }}>💵 CASH</MenuItem>
                    <MenuItem value="upi" sx={{ fontSize: '0.8rem' }}>📱 UPI</MenuItem>
                    <MenuItem value="card" sx={{ fontSize: '0.8rem' }}>💳 CARD</MenuItem>
                    <MenuItem value="bank_transfer" sx={{ fontSize: '0.8rem' }}>🏦 BANK TRANSFER</MenuItem>
                    <MenuItem value="multiple" sx={{ fontSize: '0.8rem' }}>🔄 MULTIPLE PAYMENTS</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  startIcon={<PauseIcon />}
                  onClick={handleOpenHoldDialog}
                  disabled={cart.length === 0}
                  sx={{
                    height: '32px',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    px: 1.5,
                    minWidth: '90px'
                  }}
                >
                  Hold Bill
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<PaymentIcon />}
                  onClick={() => setPaymentConfirmationDialog(true)}
                  disabled={cart.length === 0 || (multiplePayments.length > 0 && getRemainingAmount() > 0)}
                  sx={{
                    height: '32px',
                    borderRadius: '6px',
                    background: 'linear-gradient(45deg, #27ae60 30%, #229954 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #229954 30%, #1e8449 90%)'
                    },
                    boxShadow: '0 3px 8px rgba(39, 174, 96, 0.3)',
                    fontSize: '0.8rem',
                    px: 2,
                    minWidth: '100px'
                  }}
                >
                  {multiplePayments.length > 0 && getRemainingAmount() > 0 ? `₹${getRemainingAmount().toFixed(2)}` : 'Pay Now'}
                </Button>
              </Box>
              
              {/* Multiple Payments Display */}
              {multiplePayments.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  {multiplePayments.map(payment => (
                    <Chip
                      key={payment.id}
                      label={`${payment.method.toUpperCase()}: ₹${payment.amount}`}
                      onDelete={() => removePaymentMethod(payment.id)}
                      size="small"
                      sx={{ mr: 1, mb: 0.5 }}
                      color="primary"
                    />
                  ))}
                  {getRemainingAmount() > 0 && (
                    <Typography variant="caption" sx={{ color: '#e74c3c', fontSize: '0.7rem', display: 'block' }}>
                      Remaining: ₹{getRemainingAmount().toFixed(2)}
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Multiple Payments Input */}
              {showMultiplePayments && (
                <Box sx={{ p: 1, bgcolor: '#f8f9fa', borderRadius: '4px' }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={tempPaymentMethod}
                        onChange={(e) => setTempPaymentMethod(e.target.value)}
                        sx={{ fontSize: '0.8rem', height: '32px' }}
                      >
                        <MenuItem value="cash" sx={{ fontSize: '0.8rem' }}>💵 CASH</MenuItem>
                        <MenuItem value="upi" sx={{ fontSize: '0.8rem' }}>📱 UPI</MenuItem>
                        <MenuItem value="card" sx={{ fontSize: '0.8rem' }}>💳 CARD</MenuItem>
                        <MenuItem value="bank_transfer" sx={{ fontSize: '0.8rem' }}>🏦 BANK TRANSFER</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      placeholder="Amount"
                      value={tempPaymentAmount}
                      onChange={(e) => setTempPaymentAmount(e.target.value)}
                      type="number"
                      sx={{ 
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          height: '32px',
                          fontSize: '0.8rem'
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={addPaymentMethod}
                      sx={{ height: '32px', fontSize: '0.7rem' }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#6c757d', fontSize: '0.7rem' }}>
                    Total: ₹{calculateFinalPayable().toFixed(2)} | 
                    Paid: ₹{getTotalPaidAmount().toFixed(2)} | 
                    Remaining: ₹{getRemainingAmount().toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Combined Customer and Payment Dialogs in Same Row */}
      <Dialog 
        open={customerDialog || paymentDialog} 
        onClose={() => { setCustomerDialog(false); setPaymentDialog(false); }} 
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            maxWidth: '500px'
          }
        }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0,0,0,0.3)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0',
          py: 2,
          px: 3,
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            borderRadius: '20px 20px 0 0',
          }
        }}>
          <ReceiptIcon sx={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            fontSize: '1.1rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1
          }}>
            Order Processing
          </Typography>
          <IconButton
            onClick={() => { setCustomerDialog(false); setPaymentDialog(false); }}
            sx={{ 
              ml: 'auto', 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease',
              position: 'relative',
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0,
          background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
          borderRadius: '0 0 20px 20px'
        }}>
          <Box sx={{ p: 0 }}>
            <Grid container spacing={2}>
              {/* Customer Details - Full width when adding customer, 70% when paying */}
              <Grid item xs={12} md={paymentDialog ? 7 : 12} lg={paymentDialog ? 7 : 12}>
                <Box sx={{ 
                  p: 1
                }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: '#2c3e50',
                  mb: 1
                }}>
                  <PersonAddIcon sx={{ color: '#3498db' }} />
                  Customer Details
                </Typography>
                
                <TextField
                  fullWidth
                  label="Name"
                  value={customer.name}
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                
                <TextField
                  fullWidth
                  label="Phone"
                  value={customer.phone}
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                
                <TextField
                  fullWidth
                  label="Email (Optional)"
                  type="email"
                  value={customer.email}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  sx={{ mb: 2 }}
                />
                
                <Button
                  variant="contained"
                  onClick={handleSaveCustomer}
                  disabled={!customer.name || !customer.phone}
                  fullWidth
                  sx={{ 
                    borderRadius: '8px',
                    background: 'linear-gradient(45deg, #3498db 30%, #2980b9 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #2980b9 30%, #21618c 90%)'
                    },
                    boxShadow: '0 3px 8px rgba(52, 152, 219, 0.3)'
                  }}
                >
                  Save Customer
                </Button>
                </Box>
              </Grid>
              
              {/* Payment Section - Only show when paymentDialog is true */}
              {paymentDialog && (
                <Grid item xs={12} md={5} lg={5}>
                  <Box sx={{ 
                    p: 1.5,
                    background: 'linear-gradient(135deg, #f0f4f8 0%, #e1e8ed 100%)',
                    borderRadius: '12px',
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      color: '#2c3e50',
                      mb: 0.5,
                      fontWeight: 600
                    }}>
                      <PaymentIcon sx={{ color: '#27ae60', filter: 'drop-shadow(0 2px 4px rgba(39, 174, 96, 0.3))' }} />
                      Payment
                    </Typography>

                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1, mt: 1 }}>
                      Total: ₹{calculateFinalPayable().toFixed(2)}
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 1.5 }}>
                      <InputLabel>Payment Method</InputLabel>
                      <Select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        label="Payment Method"
                      >
                        <MenuItem value="cash">💵 Cash</MenuItem>
                        <MenuItem value="card">💳 Card</MenuItem>
                        <MenuItem value="upi">📱 UPI</MenuItem>
                        <MenuItem value="wallet">👛 Wallet</MenuItem>
                      </Select>
                    </FormControl>

                    {customer.name && (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        Customer: {customer.name} ({customer.phone})
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => { setCustomerDialog(false); setPaymentDialog(false); }}
                        sx={{ 
                          flex: 1,
                          borderRadius: '8px',
                          borderColor: '#e74c3c',
                          color: '#e74c3c',
                          '&:hover': {
                            borderColor: '#c0392b',
                            bgcolor: '#fee'
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => handlePayment(false)}
                        disabled={cart.length === 0}
                        sx={{ 
                          flex: 1,
                          borderRadius: '8px',
                          background: 'linear-gradient(45deg, #27ae60 30%, #229954 90%)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #229954 30%, #1e8449 90%)'
                          },
                          boxShadow: '0 3px 8px rgba(39, 174, 96, 0.3)'
                        }}
                      >
                        Complete Payment
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <Dialog
        open={paymentConfirmationDialog}
        onClose={() => setPaymentConfirmationDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            maxWidth: '500px'
          }
        }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0,0,0,0.3)'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0',
          py: 2,
          px: 3,
          boxShadow: '0 4px 20px rgba(39, 174, 96, 0.3)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            borderRadius: '20px 20px 0 0',
          }
        }}>
          <ReceiptIcon sx={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
          <Typography variant="h6" sx={{
            fontWeight: 600,
            fontSize: '1.1rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1
          }}>
            Payment Confirmation
          </Typography>
          <IconButton
            onClick={() => setPaymentConfirmationDialog(false)}
            sx={{
              ml: 'auto',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease',
              position: 'relative',
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{
          p: 3,
          background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
          borderRadius: '0 0 20px 20px'
        }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#2c3e50',
              mb: 2
            }}>
              <PaymentIcon sx={{ color: '#27ae60' }} />
              Order Summary
            </Typography>

            {/* Customer Info */}
            {selectedCustomer && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: '8px' }}>
                <Typography variant="body2" sx={{ color: '#2c3e50', fontWeight: 500 }}>
                  Customer: <span style={{ color: '#27ae60' }}>{selectedCustomer.name}</span>
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                  Phone: {selectedCustomer.phone}
                </Typography>
              </Box>
            )}

            {/* Payment Calculations */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Total Amount:</Typography>
                <Typography variant="body1">₹{calculateTotalMRP().toFixed(2)}</Typography>
              </Box>

              {calculateItemDiscount() > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: '#e74c3c' }}>
                  <Typography variant="body1">
                    Discount ({calculateTotalMRP() > 0 ? ((calculateItemDiscount() / calculateTotalMRP()) * 100).toFixed(0) : 0}%):
                  </Typography>
                  <Typography variant="body1">-₹{calculateItemDiscount().toFixed(2)}</Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">₹{calculateSaleSubtotal().toFixed(2)}</Typography>
              </Box>

              {additionalCharges.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">Additional Charges:</Typography>
                  <Typography variant="body1">₹{calculateAdditionalChargesTotal().toFixed(2)}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Final Payable:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#27ae60' }}>
                  ₹{calculateFinalPayable().toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1">Payment Method:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {getPaymentMethodDisplay()}
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setPaymentConfirmationDialog(false)}
                sx={{
                  flex: 1,
                  borderRadius: '8px',
                  borderColor: '#95a5a6',
                  color: '#95a5a6',
                  '&:hover': {
                    borderColor: '#7f8c8d',
                    bgcolor: '#f8f9fa'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setPaymentConfirmationDialog(false);
                  handlePayment(false); // Pay without printing
                }}
                sx={{
                  flex: 1,
                  borderRadius: '8px',
                  background: 'linear-gradient(45deg, #27ae60 30%, #229954 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #229954 30%, #1e8449 90%)'
                  },
                  boxShadow: '0 3px 8px rgba(39, 174, 96, 0.3)'
                }}
              >
                Pay Now
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  setPaymentConfirmationDialog(false);
                  try {
                    const billNumberResponse = await getNextBillNumber();
                    const billNumber = billNumberResponse.billNumber;
                    setCurrentOrderId(billNumber);
                    setBillPrintDialog(true); // Show bill print page
                  } catch (error) {
                    console.error('Error getting bill number:', error);
                    setSnackbar({
                      open: true,
                      message: 'Error preparing bill',
                      severity: 'error'
                    });
                  }
                }}
                sx={{
                  flex: 1,
                  borderRadius: '8px',
                  background: 'linear-gradient(45deg, #3498db 30%, #2980b9 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #2980b9 30%, #21618c 90%)'
                  },
                  boxShadow: '0 3px 8px rgba(52, 152, 219, 0.3)'
                }}
              >
                Pay & Print Bill
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Bill Print Dialog */}
      <Dialog
        open={billPrintDialog}
        onClose={() => {
          setBillPrintDialog(false);
          setCurrentOrderId(null);
        }}
        maxWidth="sm"
        fullWidth
        data-print="bill"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            maxWidth: '400px',
            fontFamily: 'monospace',
            '@media print': {
              boxShadow: 'none',
              border: 'none',
              borderRadius: 0,
              maxWidth: 'none',
              width: '100%',
              height: 'auto',
              position: 'static'
            }
          }
        }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0,0,0,0.3)',
            '@media print': {
              display: 'none'
            }
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          py: 1.5,
          px: 2,
          textAlign: 'center',
          position: 'relative',
          '@media print': {
            display: 'none'
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            borderRadius: '12px 12px 0 0',
          }
        }}>
          <Typography variant="h6" sx={{
            fontWeight: 600,
            fontSize: '1rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1,
            fontFamily: 'monospace'
          }}>
            🧾 THERMAL BILL
          </Typography>
        </DialogTitle>
        <DialogContent sx={{
          p: 0,
          background: '#f8f9fa',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          lineHeight: 1.2,
          '@media print': {
            background: 'white',
            p: 0
          }
        }}>
          <Box sx={{ 
            p: 2, 
            background: 'white', 
            mx: 1, 
            my: 1, 
            borderRadius: '8px', 
            border: '1px solid #e0e0e0',
            '@media print': {
              mx: 0,
              my: 0,
              border: 'none',
              borderRadius: 0,
              p: 0.5,
              fontSize: '0.7rem',
              lineHeight: 1.1
            }
          }}
          className="bill-print-content">
            {/* Print-specific CSS: force edge-to-edge, pure black text, and small gaps between flex columns */}
            <style>{`
              *{margin:0;padding:0;box-sizing:border-box}
              @page{margin:0;size:auto}
              html,body{width:100%;background:#ffffff;color:#000}
              body{font-family:'Helvetica Neue','Helvetica','Arial',sans-serif;font-size:13px;line-height:1.18;color:#000;background:#fff}
              .bill-print-content{width:100%;margin:0;padding:0}
              .bill-print-content *{color:#000 !important;background:transparent !important}
              /* Add small gap for any element using inline display:flex to avoid merged columns */
              .bill-print-content [style*="display:flex"]{gap:4px}
              .bill-print-content img{display:block;max-width:100%;height:auto;border:0}
              @media print{
                @page{margin:0}
                html,body{width:100% !important;max-width:none !important;margin:0 !important;padding:0 !important}
                .bill-print-content{width:100% !important;margin:0 !important;padding:0 !important}
                .bill-print-content, .bill-print-content *{-webkit-print-color-adjust:exact !important;color-adjust:exact !important;color:#000 !important;background:transparent !important}
                .bill-print-content [style*="display:flex"]{gap:4px !important}
                img{filter:none !important;box-shadow:none !important}
              }
            `}</style>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 0.5, pb: 0.5, borderBottom: '1px dashed #ccc', '@media print': { mb: 0.3, pb: 0.3 } }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.5 }}>
                🧾 {sellerInfo?.store_name || 'Store Name'}
              </Typography>
              {sellerInfo?.email && (
                <Typography sx={{ fontSize: '0.7rem', color: '#666', '@media print': { color: '#000' } }}>
                  {sellerInfo.email}
                </Typography>
              )}
              {sellerInfo?.mobile && (
                <Typography sx={{ fontSize: '0.7rem', color: '#666', '@media print': { color: '#000' } }}>
                  {sellerInfo.mobile}
                </Typography>
              )}
              {!sellerInfo && (
                <>
                  <Typography sx={{ fontSize: '0.7rem', color: '#666', '@media print': { color: '#000' } }}>123 Business Street</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#666', '@media print': { color: '#000' } }}>City, State - 123456</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#666', '@media print': { color: '#000' } }}>Phone: +91-9876543210</Typography>
                </>
              )}
            </Box>

            {/* Order Info */}
            <Box sx={{ mb: 0.5, pb: 0.5, borderBottom: '1px dashed #ccc', '@media print': { mb: 0.3, pb: 0.3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem' }}>
                  Date: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
                </Typography>
                {currentOrderId && (
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                    Bill No: #{currentOrderId}
                  </Typography>
                )}
              </Box>
              {selectedCustomer && (
                <Typography sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                  Customer: {selectedCustomer.name}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.7rem' }}>
                Payment: {getPaymentMethodString().toUpperCase()}
              </Typography>
            </Box>

            {/* Items Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, pb: 0.3, borderBottom: '1px solid #ccc', '@media print': { mb: 0.2, pb: 0.2 } }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', flex: 0.5, textAlign: 'center' }}>SNo.</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', flex: 2 }}>Item</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', textAlign: 'center', flex: 1 }}>MRP</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', textAlign: 'center', flex: 0.8 }}>Disc%</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', textAlign: 'center', flex: 0.8 }}>Qty</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', textAlign: 'right', flex: 1 }}>Total</Typography>
            </Box>

            {/* Items */}
            {cart.map((item, index) => {
              const discountPercent = item.discounted_price > 0 ? ((item.mrp - item.discounted_price) / item.mrp * 100).toFixed(0) : 0;
              const salePrice = item.discounted_price > 0 ? item.discounted_price : item.mrp;
              const totalPrice = salePrice * item.quantity;
              
              return (
                <Box key={index} sx={{ mb: 0.3, '@media print': { mb: 0.2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.6rem', flex: 0.5, textAlign: 'center' }}>{index + 1}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', flex: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.product_name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', textAlign: 'center', flex: 1 }}>₹{item.mrp.toFixed(2)}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', textAlign: 'center', flex: 0.8 }}>{discountPercent}%</Typography>
                    <Typography sx={{ fontSize: '0.6rem', textAlign: 'center', flex: 0.8 }}>
                      {item.quantity}{item.unit_short_code || item.unit_name ? ` ${item.unit_short_code || item.unit_name}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', textAlign: 'right', flex: 1 }}>₹{totalPrice.toFixed(2)}</Typography>
                  </Box>
                </Box>
              );
            })}

            {/* Separator */}
            <Box sx={{ borderTop: '1px dashed #ccc', my: 0.5, '@media print': { my: 0.3 } }} />

            {/* Totals */}
            <Box sx={{ mb: 0.5, '@media print': { mb: 0.3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2, '@media print': { mb: 0.1 } }}>
                <Typography sx={{ fontSize: '0.7rem' }}>Total Amount:</Typography>
                <Typography sx={{ fontSize: '0.7rem' }}>₹{calculateTotalMRP().toFixed(2)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2, '@media print': { mb: 0.1 } }}>
                <Typography sx={{ fontSize: '0.7rem' }}>
                  Discount ({calculateTotalMRP() > 0 ? ((calculateItemDiscount() / calculateTotalMRP()) * 100).toFixed(0) : 0}%):
                </Typography>
                <Typography sx={{ fontSize: '0.7rem' }}>-₹{calculateItemDiscount().toFixed(2)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2, '@media print': { mb: 0.1 } }}>
                <Typography sx={{ fontSize: '0.7rem' }}>Subtotal:</Typography>
                <Typography sx={{ fontSize: '0.7rem' }}>₹{calculateSaleSubtotal().toFixed(2)}</Typography>
              </Box>

              {additionalCharges.length > 0 && additionalCharges.map((charge, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2, '@media print': { mb: 0.1 } }}>
                  <Typography sx={{ fontSize: '0.7rem' }}>{charge.charge_name}:</Typography>
                  <Typography sx={{ fontSize: '0.7rem' }}>₹{Number(charge.amount).toFixed(2)}</Typography>
                </Box>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.3, borderTop: '1px solid #ccc', mt: 0.3, '@media print': { pt: 0.2, mt: 0.2 } }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Final Payable:</Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>₹{calculateFinalPayable().toFixed(2)}</Typography>
              </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', pt: 1, borderTop: '1px dashed #ccc' }}>
              <Typography sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 'bold' }}>Thank you for shopping with us!</Typography>
              <Typography sx={{ fontSize: '0.6rem', color: '#666', mb: 1 }}>Visit again soon</Typography>
              
              {/* QR Code for Play Store */}
              {qrCodeDataUrl && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                  <Box
                    component="img"
                    src={qrCodeDataUrl}
                    alt="Download our app"
                    sx={{
                      width: '70px',
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}
              <Typography sx={{ fontSize: '0.5rem', color: '#666', mb: 0.3 }}>Scan for our app</Typography>
              <Typography sx={{ fontSize: '0.5rem', color: '#666', fontWeight: 'bold' }}>Order online from mobile</Typography>
            </Box>
          </Box>

          {/* Print Buttons */}
          <Box sx={{ 
            p: 2, 
            pt: 0,
            '@media print': {
              display: 'none'
            }
          }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setBillPrintDialog(false);
                  setCurrentOrderId(null);
                  setPaymentConfirmationDialog(true);
                }}
                sx={{
                  flex: 1,
                  borderColor: '#95a5a6',
                  color: '#95a5a6',
                  '&:hover': {
                    borderColor: '#7f8c8d',
                    bgcolor: '#ecf0f1'
                  },
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  py: 1
                }}
              >
                Go Back
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    // Print the bill first (only invoice)
                    await printPOSBill();
                    
                    // Prepare fresh order data with current payment information
                    const freshOrderData = prepareOrderData();
                    
                    // Store cart before clearing for stock update
                    const cartBeforeClear = [...cart];
                    
                    // Then create the order using fresh data
                    const response = await createPOSOrder(freshOrderData);
                    
                    setSnackbar({
                      open: true,
                      message: `Order #${response.order.id} created and bill printed successfully!`,
                      severity: 'success'
                    });
                    
                    // Update stock locally without reloading all products
                    updateProductStockLocally(cartBeforeClear);
                    
                    // Reset everything
                    setCart([]);
                    setAdditionalCharges([]);
                    setCustomer({ name: '', phone: '', email: '' });
                    setCurrentOrderId(null);
                    setBillPrintDialog(false);
                    setSelectedCustomer(null);
                    setMultiplePayments([]);
                    setShowMultiplePayments(false);
                    setPaymentMethod('cash');
                  } catch (error) {
                    console.error('Error creating order:', error);
                    setSnackbar({
                      open: true,
                      message: error.response?.data?.error || 'Error processing order',
                      severity: 'error'
                    });
                  }
                }}
                sx={{
                  flex: 1,
                  background: 'linear-gradient(45deg, #27ae60 30%, #229954 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #229954 30%, #1e8449 90%)'
                  },
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  py: 1
                }}
                startIcon={<PrintIcon />}
              >
                Print & Pay
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

        {/* Hold Bill Dialog */}
        <Dialog
          open={holdDialogOpen}
          onClose={() => setHoldDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PauseIcon color="primary" /> Hold Bill
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Hold name"
                value={holdName}
                onChange={(e) => setHoldName(e.target.value)}
                placeholder="Give this held bill a name"
                fullWidth
              />
              <Box sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Summary</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Items</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Total payable</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>₹{calculateFinalPayable().toFixed(2)}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  You can restore held bills from the Held Bills page or via the POS screen.
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHoldDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveHoldBill}
              disabled={cart.length === 0}
            >
              Hold Bill
            </Button>
          </DialogActions>
        </Dialog>

      {/* Held Bills Dialog */}
      <Dialog
        open={heldBillsDialog}
        onClose={() => setHeldBillsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ListIcon color="primary" /> Held Bills ({heldBills.length})
          <IconButton
            onClick={() => setHeldBillsDialog(false)}
            sx={{ ml: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {(!heldBills || heldBills.length === 0) ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No held bills yet. Use "Hold Bill" button to save current cart.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {heldBills.map((bill) => (
                <Paper
                  key={bill.id}
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: 2,
                      borderColor: '#3498db'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {bill.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {bill.createdAt ? new Date(bill.createdAt).toLocaleString() : '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Chip 
                          label={`${bill.totals?.itemCount || 0} items`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                        <Chip 
                          label={`₹${bill.totals?.payable?.toFixed ? bill.totals.payable.toFixed(2) : bill.totals?.payable || 0}`} 
                          size="small" 
                          color="success"
                        />
                      </Box>
                      {bill.selectedCustomer && (
                        <Typography variant="caption" color="text.secondary">
                          Customer: {bill.selectedCustomer.name}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setCart(normalizeCartItems(bill.cart));
                          setAdditionalCharges(bill.additionalCharges || []);
                          setCustomer(bill.customer || { name: '', phone: '', email: '' });
                          setSelectedCustomer(bill.selectedCustomer || null);
                          setPaymentMethod(bill.paymentMethod || 'cash');
                          setMultiplePayments(bill.multiplePayments || []);
                          setShowMultiplePayments(!!bill.showMultiplePayments);
                          removeHeldBill(bill.id);
                          setHeldBills(loadHeldBills());
                          setHeldBillsDialog(false);
                          setSnackbar({ open: true, message: 'Held bill loaded. Continue billing.', severity: 'success' });
                        }}
                        sx={{ minWidth: '100px' }}
                      >
                        Resume
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                          removeHeldBill(bill.id);
                          setHeldBills(loadHeldBills());
                        }}
                        sx={{ minWidth: '100px' }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Variant Selection Dialog */}
      <Dialog
        open={variantDialog}
        onClose={() => {
          setVariantDialog(false);
          setScannedProduct(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          Select Variant - {scannedProduct?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              Choose the variant you want to add to cart:
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {scannedProduct?.variants?.map((variant) => (
                <Grid item xs={6} sm={4} md={3} lg={3} key={variant.id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      width: 180, 
                      mx: 'auto',
                      cursor: variant.stock > 0 ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease',
                      opacity: variant.stock > 0 ? 1 : 0.5,
                      '&:hover': variant.stock > 0 ? {
                        transform: 'translateY(-2px)',
                        boxShadow: 3
                      } : {}
                    }}
                    onClick={() => variant.stock > 0 && handleVariantSelect(variant)}
                  >
                    <CardMedia
                      component="img"
                      height="100"
                      image={`https://martadmin.owntrip.in/storage/${scannedProduct.image}`}
                      alt={scannedProduct.name}
                      sx={{ objectFit: 'contain', backgroundColor: 'white' }}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: '0.85rem', textAlign: 'center' }}>
                        {variant.type}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {variant.discounted_price > 0 ? (
                            <>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#7f8c8d', 
                                  fontSize: '0.6rem',
                                  textDecoration: 'line-through'
                                }}
                              >
                                ₹{variant.price}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.8rem' }}>
                                ₹{variant.discounted_price}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.8rem' }}>
                              ₹{variant.price}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: '#7f8c8d', fontSize: '0.65rem', fontWeight: 'bold' }}>
                          {variant.measurement} {variant.unit_short_code || variant.unit_name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" color={variant.stock > 0 ? "text.secondary" : "error"} sx={{ fontSize: '0.7rem' }}>
                          {variant.stock > 0 ? `Stock: ${variant.stock}` : 'Out of Stock'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {scannedProduct.type}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => {
              setVariantDialog(false);
              setScannedProduct(null);
            }}
            variant="outlined"
            fullWidth
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loose Product Dialog */}
      <Dialog
        open={looseProductDialog}
        onClose={() => {
          setLooseProductDialog(false);
          const defaultUnit = getDefaultUnit();
          setLooseProduct({ 
            name: '', 
            price: '', 
            quantity: 1, 
            unit: defaultUnit.short_code, 
            unitName: defaultUnit.name,
            pricingMethod: 'per_unit'
          });
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold',
          bgcolor: '#f8f9fa',
          color: '#2c3e50',
          borderBottom: '1px solid #e9ecef',
          fontSize: '1.1rem',
          py: 2
        }}>
          Add Loose Item
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: '#f8f9fa' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            {/* Item Name */}
            <Box>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                fontSize: '0.8rem', 
                color: '#2c3e50',
                mb: 0.5 
              }}>
                Item Name
              </Typography>
              <TextField
                value={looseProduct.name}
                onChange={(e) => setLooseProduct({ ...looseProduct, name: e.target.value })}
                fullWidth
                variant="outlined"
                size="small"
                required
                placeholder="Enter item name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e9ecef'
                    },
                    '&:hover fieldset': {
                      borderColor: '#3498db'
                    }
                  }
                }}
              />
            </Box>
            
            {/* Pricing Method */}
            <Box>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                fontSize: '0.8rem', 
                color: '#2c3e50',
                mb: 0.5 
              }}>
                Pricing Method
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant={looseProduct.pricingMethod === 'per_unit' ? 'contained' : 'outlined'}
                  onClick={() => setLooseProduct({ 
                    ...looseProduct, 
                    pricingMethod: 'per_unit',
                    price: ''
                  })}
                  sx={{
                    flex: 1,
                    height: '32px',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    ...(looseProduct.pricingMethod === 'per_unit' ? {
                      bgcolor: '#3498db',
                      '&:hover': { bgcolor: '#2980b9' }
                    } : {
                      color: '#3498db',
                      borderColor: '#3498db'
                    })
                  }}
                >
                  Price per Unit
                </Button>
                <Button
                  size="small"
                  variant={looseProduct.pricingMethod === 'total_rate' ? 'contained' : 'outlined'}
                  onClick={() => setLooseProduct({ 
                    ...looseProduct, 
                    pricingMethod: 'total_rate',
                    price: ''
                  })}
                  sx={{
                    flex: 1,
                    height: '32px',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    ...(looseProduct.pricingMethod === 'total_rate' ? {
                      bgcolor: '#3498db',
                      '&:hover': { bgcolor: '#2980b9' }
                    } : {
                      color: '#3498db',
                      borderColor: '#3498db'
                    })
                  }}
                >
                  Total Rate
                </Button>
              </Box>
            </Box>

            {/* Price and Unit Row */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '0.8rem', 
                  color: '#2c3e50',
                  mb: 0.5 
                }}>
                  {looseProduct.pricingMethod === 'total_rate' 
                    ? 'Total Rate (₹)' 
                    : `Price per ${looseProduct.unit} (₹)`}
                </Typography>
                <TextField
                  type="number"
                  value={looseProduct.price}
                  onChange={(e) => setLooseProduct({ ...looseProduct, price: e.target.value })}
                  fullWidth
                  variant="outlined"
                  size="small"
                  required
                  placeholder="0.00"
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '36px',
                      bgcolor: 'white',
                      '& fieldset': {
                        borderColor: '#e9ecef'
                      },
                      '&:hover fieldset': {
                        borderColor: '#3498db'
                      }
                    }
                  }}
                />
                {looseProduct.pricingMethod === 'total_rate' && (
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.65rem', 
                    color: '#7f8c8d',
                    mt: 0.25,
                    display: 'block'
                  }}>
                    Total for {looseProduct.quantity} {looseProduct.unit}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '0.8rem', 
                  color: '#2c3e50',
                  mb: 0.5 
                }}>
                  Unit
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={looseProduct.unit}
                    onChange={(e) => {
                      const selectedUnitShortCode = e.target.value;
                      const selectedUnit = units.find(unit => unit.short_code === selectedUnitShortCode);
                      if (selectedUnit) {
                        setLooseProduct({ 
                          ...looseProduct, 
                          unit: selectedUnit.short_code, 
                          unitName: selectedUnit.name 
                        });
                      }
                    }}
                    sx={{
                      height: '36px',
                      bgcolor: 'white',
                      '& fieldset': {
                        borderColor: '#e9ecef'
                      },
                      '&:hover fieldset': {
                        borderColor: '#3498db'
                      },
                      '& .MuiSelect-select': {
                        fontSize: '0.8rem'
                      }
                    }}
                  >
                    {units
                      .sort((a, b) => {
                        if (!a.parent_id && !b.parent_id) return a.name.localeCompare(b.name);
                        if (!a.parent_id) return -1;
                        if (!b.parent_id) return 1;
                        if (a.parent_id === b.parent_id) return a.name.localeCompare(b.name);
                        return a.parent_id - b.parent_id;
                      })
                      .map((unit) => (
                        <MenuItem key={unit.id} value={unit.short_code} sx={{ fontSize: '0.8rem' }}>
                          {unit.parent_id ? '  ↳ ' : ''}{unit.name} ({unit.short_code})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Quantity */}
            <Box>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                fontSize: '0.8rem', 
                color: '#2c3e50',
                mb: 0.5 
              }}>
                Quantity ({looseProduct.unit})
              </Typography>
              <TextField
                type="number"
                value={looseProduct.quantity}
                onChange={(e) => setLooseProduct({ ...looseProduct, quantity: e.target.value })}
                fullWidth
                variant="outlined"
                size="small"
                required
                placeholder="1"
                inputProps={{ 
                  min: 0.01, 
                  step: ['gm', 'mg', 'ml', 'cl'].includes(looseProduct.unit) ? 0.01 : 
                        ['kg', 'ltr', 'l', 'ton', 'qtl'].includes(looseProduct.unit) ? 0.1 : 
                        0.01 
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    bgcolor: 'white',
                    '& fieldset': {
                      borderColor: '#e9ecef'
                    },
                    '&:hover fieldset': {
                      borderColor: '#3498db'
                    }
                  }
                }}
              />
            </Box>

            {/* Summary */}
            {looseProduct.name && looseProduct.price && looseProduct.quantity && (
              <Box sx={{ 
                p: 1.5, 
                bgcolor: 'white', 
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 'bold', 
                  fontSize: '0.8rem', 
                  color: '#2c3e50',
                  mb: 1
                }}>
                  Summary:
                </Typography>
                {looseProduct.pricingMethod === 'total_rate' ? (
                  <>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5, color: '#34495e' }}>
                      {looseProduct.quantity} {looseProduct.unit} for ₹{looseProduct.price}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#7f8c8d', mb: 1, display: 'block' }}>
                      Rate per {looseProduct.unit}: ₹{(parseFloat(looseProduct.price || 0) / parseFloat(looseProduct.quantity || 1)).toFixed(4)}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      bgcolor: '#e8f5e8',
                      px: 1,
                      py: 0.5,
                      borderRadius: '4px'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        Total Amount:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#27ae60', fontSize: '0.9rem' }}>
                        ₹{parseFloat(looseProduct.price || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 1, color: '#34495e' }}>
                      {looseProduct.quantity} {looseProduct.unit} × ₹{looseProduct.price} per {looseProduct.unit}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      bgcolor: '#e8f5e8',
                      px: 1,
                      py: 0.5,
                      borderRadius: '4px'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                        Total Amount:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#27ae60', fontSize: '0.9rem' }}>
                        ₹{(parseFloat(looseProduct.price || 0) * parseFloat(looseProduct.quantity || 0)).toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, bgcolor: '#f8f9fa', gap: 1 }}>
          <Button
            onClick={() => {
              setLooseProductDialog(false);
              const defaultUnit = getDefaultUnit();
              setLooseProduct({ 
                name: '', 
                price: '', 
                quantity: 1, 
                unit: defaultUnit.short_code, 
                unitName: defaultUnit.name,
                pricingMethod: 'per_unit'
              });
            }}
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              height: '36px',
              fontSize: '0.8rem',
              textTransform: 'none',
              color: '#7f8c8d',
              borderColor: '#bdc3c7',
              '&:hover': {
                borderColor: '#95a5a6',
                bgcolor: '#ecf0f1'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={addLooseProduct}
            variant="contained"
            size="small"
            disabled={!looseProduct.name || !looseProduct.price || !looseProduct.quantity}
            sx={{
              flex: 1,
              height: '36px',
              fontSize: '0.8rem',
              textTransform: 'none',
              bgcolor: '#27ae60',
              '&:hover': { bgcolor: '#229954' },
              '&:disabled': { 
                bgcolor: '#bdc3c7',
                color: '#7f8c8d'
              }
            }}
          >
            Add to Cart
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Detail Dialog */}
      <Dialog 
        open={productDetailDialog} 
        onClose={() => setProductDetailDialog(false)} 
        maxWidth="lg" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }
        }}
      >
        {selectedProductDetail && (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '12px 12px 0 0',
              mb: 0
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {selectedProductDetail.name}
              </Typography>
              <IconButton 
                onClick={() => setProductDetailDialog(false)} 
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 4, mb: 3, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1.5, display: 'flex', justifyContent: 'center' }}>
                  <img 
                    src={`https://martadmin.owntrip.in/storage/${selectedProductDetail.image}`} 
                    alt={selectedProductDetail.name} 
                    style={{ 
                      width: '100%', 
                      height: 'auto', 
                      maxHeight: 500, 
                      objectFit: 'contain', 
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }} 
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Product Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 100 }}>Name:</Typography>
                        <Typography variant="body1" sx={{ ml: 1 }}>{selectedProductDetail.name}</Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 100 }}>Price:</Typography>
                        <Box sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {selectedProductDetail.variants?.[0]?.discounted_price > 0 ? (
                            <>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  color: '#7f8c8d', 
                                  fontSize: '0.9rem',
                                  textDecoration: 'line-through'
                                }}
                              >
                                ₹{selectedProductDetail.variants?.[0]?.price}
                              </Typography>
                              <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                ₹{selectedProductDetail.variants?.[0]?.discounted_price}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                              ₹{selectedProductDetail.variants?.[0]?.price ?? 'N/A'}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 100 }}>Stock:</Typography>
                        <Typography
                          variant="body1"
                          sx={{ ml: 1, color: ((selectedProductDetail.variants?.reduce((a, v) => a + (Number(v.stock) || 0), 0) || 0) > 0 ? 'success.main' : 'error.main') }}
                        >
                          {selectedProductDetail.variants?.reduce((a, v) => a + (Number(v.stock) || 0), 0) ?? 0}
                        </Typography>
                      </Box>

                      {/* Variants list */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Variants</Typography>
                        {selectedProductDetail.variants && selectedProductDetail.variants.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {selectedProductDetail.variants.map((v) => (
                              <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: 1, bgcolor: 'background.paper', boxShadow: 1 }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{v.type}</Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{v.measurement} {v.unit_short_code || ''}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  {Number(v.discounted_price) > 0 ? (
                                    <>
                                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#7f8c8d' }}>₹{v.price}</Typography>
                                      <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>₹{v.discounted_price}</Typography>
                                    </>
                                  ) : (
                                    <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>₹{v.price}</Typography>
                                  )}
                                  <Typography variant="body2" sx={{ color: (Number(v.stock) > 0 ? 'success.main' : 'error.main'), fontSize: '0.85rem' }}>Stock: {Number(v.stock)}</Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No variants available</Typography>
                        )}
                      </Box>

                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 100 }}>Type:</Typography>
                        <Typography variant="body1" sx={{ ml: 1 }}>{selectedProductDetail.type}</Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 100 }}>Status:</Typography>
                        <Typography variant="body1" sx={{ ml: 1, color: selectedProductDetail.status ? 'success.main' : 'error.main' }}>
                          {selectedProductDetail.status ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 100 }}>Barcode:</Typography>
                        <Typography variant="body1" sx={{ ml: 1 }}>{selectedProductDetail.barcode}</Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 100 }}>Brand:</Typography>
                        <Typography variant="body1" sx={{ ml: 1 }}>{selectedProductDetail.brand_name || selectedProductDetail.brand_id}</Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 100 }}>Category:</Typography>
                        <Typography variant="body1" sx={{ ml: 1 }}>{selectedProductDetail.category_id}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 120 }}>Manufacturer:</Typography>
                        <Typography variant="body1" sx={{ ml: 1 }}>{selectedProductDetail.manufacturer}</Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 120 }}>Made In:</Typography>
                        <Typography variant="body1" sx={{ ml: 1 }}>{selectedProductDetail.made_in}</Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 120 }}>FSSAI No:</Typography>
                        <Typography variant="body1" sx={{ ml: 1 }}>{selectedProductDetail.fssai_lic_no}</Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 120 }}>Unlimited Stock:</Typography>
                        <Typography variant="body1" sx={{ ml: 1, color: selectedProductDetail.is_unlimited_stock ? 'success.main' : 'error.main' }}>
                          {selectedProductDetail.is_unlimited_stock ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 120 }}>Tax Included:</Typography>
                        <Typography variant="body1" sx={{ ml: 1, color: selectedProductDetail.tax_included_in_price ? 'success.main' : 'error.main' }}>
                          {selectedProductDetail.tax_included_in_price ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 120 }}>Tax ID:</Typography>
                        <Typography variant="body1" sx={{ ml: 1 }}>{selectedProductDetail.tax_id}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setProductDetailDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

    </Box>
  );
}